import React from 'react';
import { motion } from 'framer-motion';
import { Section, SectionTitle } from '@/components/shared/Section';
import { fadeUp, stagger } from '@/lib/motion';
import { APLICACIONES, CARACTERISTICAS, ERP_LOGOS } from '@/data/venddi';
import { Receipt, CreditCard, Database, Rocket } from 'lucide-react';

export function VenddiAplicaciones() {
  return (
    <Section>
      <SectionTitle
        badge="APLICACIONES"
        title="Dónde Venddi brilla más"
        subtitle="Un nuevo producto ALLCOM pensado para el retail del futuro: autoservicio, inteligencia artificial y operación remota. Estas son las industrias donde genera mayor valor desde el día uno."
        center
      />
      {/* Vision-of-future banner */}
      <div className="max-w-4xl mx-auto mb-8 p-5 rounded-2xl bg-gradient-to-br from-sky-50 to-white border border-sky-200 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#38BDF8]/20 flex items-center justify-center shrink-0">
          <Rocket className="w-5 h-5 text-[#0EA5E9]" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0EA5E9] mb-1">Nuevo producto · Visión de futuro</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Venddi POS es el primer software chileno nativamente diseñado para la economía del <strong className="text-[#0A2540]">self-service + IA</strong>. Está pensado para los próximos 10 años del retail, no para los últimos 10. Estás entre los primeros en adoptarlo.
          </p>
        </div>
      </div>
      <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
        {APLICACIONES.map((a, i) => (
          <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${a.color}15` }}>
              <a.icon className="w-6 h-6" strokeWidth={1.8} style={{ color: a.color }} />
            </div>
            <h3 className="text-base font-bold text-[#0A2540] mb-1" style={{ fontFamily: 'Syne,sans-serif' }}>{a.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
}

export function VenddiIntegraciones() {
  const bloques = [
    { icon: Receipt, title: 'SII Chile', desc: 'Timbre electrónico directo + boleta + factura', color: '#0A2540', chip: 'OFICIAL' },
    { icon: CreditCard, title: 'Medios de Pago', desc: 'Transbank, Getnet, SumUp, Mercado Pago', color: '#0EA5E9', chip: 'MULTI-ACQUIRER' },
    { icon: Database, title: 'ERPs', desc: '12+ conectores nativos listos en 48h', color: '#10B981', chip: '20+ SISTEMAS' },
  ];
  return (
    <Section alt>
      <SectionTitle
        badge="INTEGRACIONES NATIVAS"
        title="Conectado con Chile de verdad"
        subtitle="SII, Transbank, Getnet y los principales ERPs del mercado. Sin middleware, sin intermediarios, sin sorpresas."
        center
      />
      <div className="max-w-5xl mx-auto">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {bloques.map((c, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white border-2 relative" style={{ borderColor: c.color }}>
              <span className="absolute -top-2.5 left-4 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-full text-white" style={{ background: c.color }}>{c.chip}</span>
              <c.icon className="w-10 h-10 mb-3" strokeWidth={1.5} style={{ color: c.color }} />
              <h3 className="text-lg font-bold text-[#0A2540] mb-1" style={{ fontFamily: 'Syne,sans-serif' }}>{c.title}</h3>
              <p className="text-sm text-slate-600">{c.desc}</p>
            </div>
          ))}
        </div>
        <div className="p-6 rounded-2xl bg-white border border-slate-200">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 text-center">ERPs con conector nativo</p>
          <div className="flex flex-wrap justify-center gap-2">
            {ERP_LOGOS.map((e) => (
              <span key={e} className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-700">{e}</span>
            ))}
            <span className="px-3 py-1.5 rounded-lg bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 text-xs font-bold text-[#0EA5E9]">+ tu ERP custom</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

export function VenddiCaracteristicas() {
  return (
    <Section>
      <SectionTitle
        badge="CARACTERÍSTICAS"
        title="Todo lo que hace Venddi"
        subtitle="Un software POS completo, pensado para operaciones de autoservicio reales, no un software genérico adaptado."
        center
      />
      <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
        {CARACTERISTICAS.map((c, i) => (
          <motion.div key={i} variants={fadeUp} className="p-5 rounded-xl bg-white border border-slate-200 hover:border-[#0EA5E9]/40 hover:shadow-md transition-all">
            <div className="flex items-start gap-3">
              <c.icon className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5" strokeWidth={1.8} />
              <div>
                <h3 className="text-sm font-bold text-[#0A2540] mb-1" style={{ fontFamily: 'Syne,sans-serif' }}>{c.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
}
